import 'codemirror/lib/codemirror.css';
import 'codemirror/mode/javascript/javascript';
import 'codemirror/addon/display/autorefresh';
import 'codemirror/addon/fold/brace-fold';
import 'codemirror/addon/fold/foldcode';
import 'codemirror/addon/fold/foldgutter';
import 'codemirror/addon/fold/foldgutter.css';
import 'codemirror/theme/base16-light.css';
import 'codemirror/theme/base16-dark.css';
import 'codemirror/addon/lint/lint.css';
import 'codemirror/addon/lint/lint';
import 'codemirror/addon/lint/javascript-lint';
import 'codemirror/addon/hint/show-hint';
import 'codemirror/addon/hint/show-hint.css';
import styles from './CodeMirror.scss';
import isEqual from 'lodash.isequal';
import debounce from 'lodash.debounce';
import React, { useState, useEffect, useRef, useCallback, useImperativeHandle, forwardRef, useMemo, } from 'react';
import classnames from 'classnames';
const cx = classnames.bind(styles);
function normalizeLineEndings(str) {
    if (!str)
        return str;
    return str.replace(/\r\n|\r/g, '\n');
}
const CodeMirror = forwardRef(function CodeMirror({ autoFocus, className, codeMirrorInstance: codeMirrorInstanceProp, defaultValue, name, onChange, onCursorActivity, onFocusChange, onScroll, options, path, value, preserveScrollPosition = false, hintItems, }, ref) {
    const [isFocused, setIsFocused] = useState(false);
    const [showingSnippetDescription, setShowingSnippetDescription] = useState(false);
    const [currentHint, setCurrentHint] = useState(null);
    const textareaRef = useRef(null);
    const codeMirrorRef = useRef(null);
    const previousValueRef = useRef(value);
    const previousOptionsRef = useRef(options);
    const hintItemsRef = useRef(hintItems);
    // Keep hintItemsRef in sync with the prop so event handlers always have current value
    useEffect(() => {
        hintItemsRef.current = hintItems;
    }, [hintItems]);
    const getCodeMirrorInstance = useCallback(() => {
        return codeMirrorInstanceProp || require('codemirror');
    }, [codeMirrorInstanceProp]);
    // Expose methods via ref
    useImperativeHandle(ref, () => ({
        getCodeMirror: () => codeMirrorRef.current,
        get codeMirror() {
            return codeMirrorRef.current;
        },
        focus: () => {
            if (codeMirrorRef.current) {
                codeMirrorRef.current.focus();
            }
        },
    }), []);
    const setOptionIfChanged = useCallback((optionName, newValue) => {
        if (!codeMirrorRef.current)
            return;
        const oldValue = codeMirrorRef.current.getOption(optionName);
        if (!isEqual(oldValue, newValue)) {
            codeMirrorRef.current.setOption(optionName, newValue);
        }
    }, []);
    const focusChanged = useCallback((focused) => {
        setIsFocused(focused);
        onFocusChange && onFocusChange(focused);
    }, [onFocusChange]);
    const cursorActivity = useCallback((cm) => {
        onCursorActivity && onCursorActivity(cm);
    }, [onCursorActivity]);
    const scrollChanged = useCallback((cm) => {
        onScroll && onScroll(cm.getScrollInfo());
    }, [onScroll]);
    const codemirrorValueChanged = useCallback((doc, change) => {
        if (onChange && change.origin !== 'setValue') {
            onChange(doc.getValue(), change);
        }
    }, [onChange]);
    const onCloseHints = useCallback(() => {
        setShowingSnippetDescription(false);
    }, []);
    const onSelectHint = useCallback((hint) => {
        setCurrentHint(hint);
    }, []);
    const showHints = useCallback((currentKeyword) => {
        if (!hintItemsRef.current)
            return;
        const hintNamespace = hintItemsRef.current.find((i) => i.namespace === currentKeyword);
        if (!hintNamespace)
            return;
        const snippets = hintNamespace.items.map((i) => ({
            ...i,
            render: (element) => {
                const hintText = document.createElement('div');
                hintText.className = 'hintText';
                hintText.textContent = i.displayText;
                element.appendChild(hintText);
                const descriptionIcon = document.createElement('div');
                descriptionIcon.className = 'descriptionIcon';
                descriptionIcon.textContent = 'i';
                element.appendChild(descriptionIcon);
                descriptionIcon.addEventListener('click', (evt) => {
                    evt.stopPropagation();
                    setShowingSnippetDescription(true);
                });
            },
        }));
        const cmInstance = getCodeMirrorInstance();
        cmInstance.showHint(codeMirrorRef.current, () => {
            const cursor = codeMirrorRef.current.getCursor();
            const token = codeMirrorRef.current.getTokenAt(cursor);
            const start = token.start;
            const end = cursor.ch;
            const line = cursor.line;
            const currentWord = token.string;
            const tokens = codeMirrorRef.current.getLineTokens(line);
            const list = snippets.filter(function (item) {
                return item.displayText.indexOf(currentWord) >= 0;
            });
            let from;
            if (tokens[tokens.length - 2]?.string === '.') {
                from = cmInstance.Pos(line, tokens[tokens.length - 2].start);
            }
            else {
                from = cmInstance.Pos(line, start);
            }
            const result = {
                list: list.length ? list : snippets,
                from,
                to: cmInstance.Pos(line, end),
            };
            cmInstance.on(result, 'close', onCloseHints);
            cmInstance.on(result, 'select', onSelectHint);
            return result;
        }, {
            completeSingle: false,
        });
    }, [getCodeMirrorInstance, onCloseHints, onSelectHint]);
    const checkHint = useCallback((cm, event) => {
        if (!hintItemsRef.current)
            return;
        const currentLineValue = cm.getLine(cm.getCursor().line);
        const namespaces = hintItemsRef.current.map((i) => i.namespace);
        const currentKeyword = namespaces.find((k) => currentLineValue.includes(k));
        if (!cm.state.completionActive &&
            event.keyCode !== 13 &&
            currentKeyword &&
            !/\(|\)/.test(currentLineValue)) {
            showHints(currentKeyword);
        }
    }, [showHints]);
    // Initialize CodeMirror
    useEffect(() => {
        const cmInstance = getCodeMirrorInstance();
        codeMirrorRef.current = cmInstance.fromTextArea(textareaRef.current, options);
        codeMirrorRef.current.on('change', codemirrorValueChanged);
        codeMirrorRef.current.on('cursorActivity', cursorActivity);
        codeMirrorRef.current.on('focus', () => focusChanged(true));
        codeMirrorRef.current.on('blur', () => focusChanged(false));
        codeMirrorRef.current.on('scroll', scrollChanged);
        codeMirrorRef.current.on('keyup', checkHint);
        codeMirrorRef.current.setValue(defaultValue || value || '');
        return () => {
            if (codeMirrorRef.current) {
                codeMirrorRef.current.toTextArea();
            }
        };
        // Only run on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Debounced value update effect
    const debouncedUpdateValue = useMemo(() => debounce((newValue, prevValue, preserve) => {
        if (codeMirrorRef.current &&
            newValue !== undefined &&
            newValue !== prevValue &&
            normalizeLineEndings(codeMirrorRef.current.getValue()) !==
                normalizeLineEndings(newValue)) {
            if (preserve) {
                const prevScrollPosition = codeMirrorRef.current.getScrollInfo();
                codeMirrorRef.current.setValue(newValue);
                codeMirrorRef.current.scrollTo(prevScrollPosition.left, prevScrollPosition.top);
            }
            else {
                codeMirrorRef.current.setValue(newValue);
            }
        }
    }, 0), []);
    // Handle value changes
    useEffect(() => {
        debouncedUpdateValue(value, previousValueRef.current, preserveScrollPosition);
        previousValueRef.current = value;
    }, [value, preserveScrollPosition, debouncedUpdateValue]);
    // Handle options changes
    useEffect(() => {
        if (typeof options === 'object' && previousOptionsRef.current !== options) {
            for (const optionName in options) {
                if (Object.prototype.hasOwnProperty.call(options, optionName)) {
                    setOptionIfChanged(optionName, options[optionName]);
                }
            }
            previousOptionsRef.current = options;
        }
    }, [options, setOptionIfChanged]);
    const renderDescription = () => {
        if (showingSnippetDescription && currentHint) {
            const hintsElement = document.getElementsByClassName('CodeMirror-hints');
            if (!hintsElement[0])
                return null;
            const hintsEl = hintsElement[0];
            return (React.createElement("div", { className: cx('snippetDescription'), style: {
                    top: `${hintsEl.offsetTop}px`,
                    left: `${hintsEl.offsetLeft + hintsEl.offsetWidth}px`,
                } },
                React.createElement("div", { className: cx('header') },
                    React.createElement("h2", { className: cx('title') }, currentHint.displayText)),
                React.createElement("div", { className: cx('body') }, currentHint.description)));
        }
        return null;
    };
    return (React.createElement("div", { className: classnames('ReactCodeMirror', className, {
            'ReactCodeMirror--focused': isFocused,
        }) },
        renderDescription(),
        React.createElement("textarea", { ref: textareaRef, name: name || path, defaultValue: value, autoComplete: "off", autoFocus: autoFocus })));
});
export default CodeMirror;
